import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import { visuallyHidden } from "@mui/utils";
import { GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useState } from "react";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) {
  const stabilizedThis = array?.map((el, index) => [el, index] as [T, number]);
  stabilizedThis?.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis?.map((el) => el[0]);
}

interface EnhancedTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: string | number
  ) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

export default function CustomTable<T extends { [key: string]: any }>({
  onRowClick,
  orderBy = "id",
  data,
  columns,
  removeCellBorder = false,
  removeTableHeader = false,
  hasGrayHeader = false,
  headerSize = "medium",
  enableHeaderSorting = true,
  roundedHeader = "none",
  fetchMore,
  compact = false,
  enableRowHighlight = true,
  tableOrder = "asc",
  paginationIncluded = false,
  rowsPerPageOptions = [10, 15, 25],
  hasTableBorder = false,
  emptyMessage,
}: {
  data: T[];
  onRowClick?: (data: T, column?: GridColDef<any, any, any>) => void;
  orderBy: string;
  dataKey: string;
  removeTableHeader?: boolean;
  removeCellBorder?: boolean;
  columns: GridColDef[];
  hasGrayHeader?: boolean;
  headerSize?: "small" | "medium";
  enableHeaderSorting?: boolean;
  roundedHeader?: "none" | "xs" | "sm" | "md" | "lg" | "xl" | "full";
  fetchMore?: () => void;
  compact?: boolean;
  enableRowHighlight?: boolean;
  tableOrder?: Order;
  paginationIncluded?: boolean;
  rowsPerPageOptions?: number[];
  hasTableBorder?: boolean;
  emptyMessage?: string;
}) {
  const [order, setOrder] = useState<Order>(tableOrder as Order);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0] || 10);
  const [selected, setSelected] = useState<number>();
  function EnhancedTableHead(props: EnhancedTableProps) {
    const { order, orderBy, onRequestSort } = props;
    const createSortHandler =
      (property: string | number) => (event: React.MouseEvent<unknown>) => {
        onRequestSort(event, property);
      };
    const { t } = useTranslation();

    const headerClassName = compact
      ? "text-xs font-medium h-full text-[#29333D]"
      : "text-[1.25rem] font-semibold h-full text-[#29333D]";

    return (
      <TableHead>
        <TableRow
          sx={{
            backgroundColor: hasGrayHeader ? "#F0F2F5" : "white",
            borderRadius:
              roundedHeader === "xs"
                ? "0.37rem"
                : roundedHeader === "sm"
                ? "0.55rem"
                : roundedHeader === "md"
                ? "0.75rem"
                : roundedHeader === "lg"
                ? "1rem"
                : roundedHeader === "full"
                ? "1.5rem"
                : 0,
          }}
        >
          {columns?.map((headCell, index) => (
            <TableCell
              key={`${headCell.field}-${index}`}
              padding={"normal"}
              sx={{ backgroundColor: hasGrayHeader ? "#F0F2F5" : "white" }}
              sortDirection={orderBy === headCell.field ? order : false}
            >
              {!enableHeaderSorting && (
                <label className={headerClassName}>
                  {headCell.renderHeader
                    ? headCell.renderHeader({ colDef: headCell } as any)
                    : String(headCell.headerName)}
                </label>
              )}
              {enableHeaderSorting && (
                <TableSortLabel
                  active={orderBy === headCell.field}
                  direction={orderBy === headCell.field ? order : "asc"}
                  onClick={createSortHandler(headCell.field)}
                  className={headerClassName}
                >
                  {headCell.renderHeader
                    ? headCell.renderHeader({ colDef: headCell } as any)
                    : String(headCell.headerName)}
                  {orderBy === headCell.field ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === "desc"
                        ? t("CustomTable.sorted-dsc")
                        : t("CustomTable.sorted-asc")}
                    </Box>
                  ) : null}
                </TableSortLabel>
              )}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  }
  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string | number
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
  };
  const handleScroll = (e: any) => {
    const target = e.target;
    const isAtBottom =
      Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) <
      1;
    if (isAtBottom && fetchMore) {
      fetchMore();
    }
  };
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };
  const sortedData = stableSort(data, getComparator(order, orderBy));
  const renderedData = paginationIncluded
    ? sortedData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : sortedData;

  const { t } = useTranslation();

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2, height: "100%", boxShadow: "none" }}>
        <TableContainer
          onScroll={handleScroll}
          sx={{
            maxHeight: "100%",
            border: hasTableBorder ? "1px solid #e0e0e0" : "none",
            borderRadius: hasTableBorder ? "0.5rem" : 0,
          }}
        >
          <Table
            stickyHeader
            sx={{ minWidth: "fit-content" }}
            aria-labelledby="tableTitle"
            size={headerSize}
          >
            {!removeTableHeader && (
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={data?.length}
              />
            )}
            <TableBody>
              {emptyMessage && renderedData?.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={columns?.length || 1}
                    align="center"
                    data-testid="custom-table-empty"
                    sx={{ borderBottom: "none", py: 6, color: "#667085" }}
                  >
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              )}
              {renderedData?.map(
                (row, rIndex) => {
                  return (
                    <TableRow
                      role="checkbox"
                      tabIndex={-1}
                      key={`row-${rIndex}`}
                      sx={{ cursor: "pointer" }}
                      className={
                        enableRowHighlight
                          ? `${
                              selected === rIndex
                                ? "bg-blue-200"
                                : "hover:bg-[#eff6ffb6]"
                            }`
                          : "hover:bg-[#eff6ffb6]"
                      }
                    >
                      {columns?.map((column, cIndex) => {
                        let value: number | string | boolean;

                        const cellSx = compact
                          ? {
                              fontSize: "0.875rem",
                              paddingTop: "10px",
                              paddingBottom: "10px",
                            }
                          : {
                              fontSize: "1rem",
                              "@media (min-width: 1024px)": {
                                fontSize: "1.125rem",
                              },
                              "@media (min-width: 1280px)": {
                                fontSize: "1.25rem",
                              },
                            };

                        if (column.renderCell) {
                          return (
                            <TableCell
                              key={`cell-${rIndex}-${cIndex}`}
                              onClick={() => {
                                if (enableRowHighlight) {
                                  setSelected(rIndex);
                                }
                                onRowClick?.(row as T, column);
                              }}
                              sx={{
                                borderBottom: removeCellBorder
                                  ? "none"
                                  : "1px solid #e0e0e0",
                                flex: `${column.flex}`,
                                ...cellSx,
                              }}
                              width={column.width ?? "auto"}
                              align={column.align ?? "left"}
                            >
                              {column.renderCell({
                                row,
                              } as any as GridValueGetterParams<any>)}
                            </TableCell>
                          );
                        } else {
                          if (column.valueGetter) {
                            value = column.valueGetter({
                              row: {
                                [column.field]: row[column.field],
                              },
                            } as GridValueGetterParams<any>);
                          } else {
                            value = row[column.field];
                          }

                          return (
                            <TableCell
                              key={`cell-${rIndex}-${cIndex}`}
                              onClick={() => {
                                if (enableRowHighlight) {
                                  setSelected(rIndex);
                                }
                                onRowClick?.(row as T, column);
                              }}
                              align={column.align ?? "left"}
                              sx={{
                                borderBottom: removeCellBorder
                                  ? "none"
                                  : "1px solid #e0e0e0",
                                flex: `${column.flex}`,
                                ...cellSx,
                              }}
                              width={column.width ?? "auto"}
                            >
                              {typeof value === "boolean"
                                ? value
                                  ? "✔"
                                  : "✖"
                                : value?.toString() ?? ""}
                            </TableCell>
                          );
                        }
                      })}
                    </TableRow>
                  );
                }
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {paginationIncluded && (
          <TablePagination
            component="div"
            count={data.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={rowsPerPageOptions}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={({ from, to, count }) => {
              return `${from}–${to} ${t("labels.of")} ${
                count !== -1 ? count : `${t("labels.more-than")} ${to}`
              }`;
            }}
            labelRowsPerPage={t("labels.rows-per-page")}
            sx={{
              "& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel":
                {
                  fontSize: "1rem",

                  "@media (min-width: 1024px)": {
                    fontSize: "1.125rem",
                  },

                  "@media (min-width: 1280px)": {
                    fontSize: "1.25rem",
                  },
                },

              "& .MuiTablePagination-select": {
                fontSize: "1rem",

                "@media (min-width: 1024px)": {
                  fontSize: "1.125rem",
                },

                "@media (min-width: 1280px)": {
                  fontSize: "1.25rem",
                },
              },
            }}
          />
        )}
      </Paper>
    </Box>
  );
}
